import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Download } from '@factorialco/f0-react/icons/app'
import { useApolloClient } from '@apollo/client'
import ReactPDF from '@react-pdf/renderer'
import moment from 'moment'
import { useCallback, useState } from 'react'
import { fetchEnvironment } from 'config/environment'
import {
  ContactFragment,
  PrintablePurchaseOrderDocument,
  PurchaseOrdersDataCollectionDocument,
  PurchaseOrderUpdatedSubscriptionDocument,
  type PurchaseOrderItemFragment,
  type ProcurementValueObjectsPurchaseOrderStatusEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import { buildClient } from 'modules/core/lib/graphql/buildClient'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'
import app from 'modules/core/store/models/app'

import { contactFullName } from 'modules/finance/lib/contactsUtils'
import { useContactsFilter } from 'modules/finance/lib/filters/contacts'
import PrintablePurchaseOrder from 'modules/procurement/components/purchaseOrders/PrintablePurchaseOrder'
import { ExportSidepanel } from 'modules/universal_exports/components/ExportSidepanel'

type StatusType = 'positive' | 'critical' | 'warning' | 'neutral' | 'info'

const PO_STATUS_VARIANT: Record<ProcurementValueObjectsPurchaseOrderStatusEnum, StatusType> = {
  processing: 'neutral',
  pending: 'warning',
  ordered: 'info',
  partial: 'info',
  received: 'info',
  closed: 'neutral',
  draft: 'neutral',
}

const isValidPoStatus = (value: unknown): value is ProcurementValueObjectsPurchaseOrderStatusEnum =>
  typeof value === 'string' && value in PO_STATUS_VARIANT

interface Props {
  vendor?: ContactFragment
  canExport?: boolean
  onVisibleV2IdsChange?: (ids: string[]) => void
}

const PurchaseOrdersDataTable = ({ vendor, canExport, onVisibleV2IdsChange }: Props) => {
  const [showExportSidepanel, setShowExportSidepanel] = useState(false)
  const {
    company,
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useNavigation()

  // Ordered V2 IDs the user currently sees — feeds F0Sidepanel prev/next.
  const updateVisibleV2Ids = useCallback(
    (nodes: PurchaseOrderItemFragment[]) => {
      if (!onVisibleV2IdsChange) return
      const v2Ids = nodes.filter((n) => n.poTemplateVersion).map((n) => n.id)
      onVisibleV2IdsChange(v2Ids)
    },
    [onVisibleV2IdsChange]
  )

  const vendorFilter = useContactsFilter({
    label: i18n.t('procurement.purchase_orders.list.vendor'),
    contactType: 'vendor',
  })

  const getPoStatusLabel = (status: ProcurementValueObjectsPurchaseOrderStatusEnum): string => {
    const labels: Record<ProcurementValueObjectsPurchaseOrderStatusEnum, string> = {
      processing: i18n.t('procurement.purchase_orders.status.processing'),
      pending: i18n.t('procurement.purchase_orders.status.pending'),
      ordered: i18n.t('procurement.purchase_orders.status.ordered'),
      partial: i18n.t('procurement.purchase_orders.status.partial'),
      received: i18n.t('procurement.purchase_orders.status.received'),
      closed: i18n.t('procurement.purchase_orders.status.closed'),
      draft: i18n.t('procurement.purchase_orders.status.draft'),
    }
    return labels[status]
  }

  const handleDownloadPdf = async (id: string) => {
    const environment = await fetchEnvironment()
    const graphQLClient = await buildClient(environment)

    const { data, error } = await graphQLClient.query({
      query: PrintablePurchaseOrderDocument,
      variables: { id, companyId: company.id },
    })

    const purchaseOrder = data.procurement.purchaseOrdersConnection.edges[0]?.node

    if (error || !purchaseOrder) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('procurement.purchase_orders.pdf.fetch_error'),
      })
      return
    }

    const pdf = ReactPDF.pdf(
      <PrintablePurchaseOrder
        locale={locale}
        purchaseOrder={purchaseOrder}
        logo={company.logo ?? null}
      />
    )
    const blob = await pdf.toBlob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')

    a.href = url
    a.download = purchaseOrder.formattedPoNumber
    a.click()
  }

  const source = useGraphqlDataCollectionSource({
    document: PurchaseOrdersDataCollectionDocument,
    mapData: (data) => {
      const nodes = data.procurement.purchaseOrdersConnection.edges.map((edge) => edge.node) ?? []
      updateVisibleV2Ids(nodes)
      return nodes
    },
    mapPagination: (data) => ({
      total: data.procurement.purchaseOrdersConnection.totalCount ?? 0,
    }),
    mapVariables: ({ sortings, filters, search }) => ({
      companyId: company.id,
      search: search ?? undefined,
      vendorIds: vendor ? [vendor.id] : filters.vendor?.length ? filters.vendor : undefined,
      status: isValidPoStatus(filters.statusPreset?.[0]) ? filters.statusPreset[0] : undefined,
      sortOrder: sortings?.[0] ?? { field: 'description', order: 'asc' },
      filter: {
        status: filters.status?.length ? { in: filters.status } : undefined,
        date:
          filters.date && typeof filters.date === 'object' && 'from' in filters.date
            ? {
                between: [
                  moment(filters.date.from).startOf('day').toISOString(),
                  moment(filters.date.to ?? filters.date.from)
                    .endOf('day')
                    .toISOString(),
                ],
              }
            : undefined,
      },
    }),
    search: { enabled: true },
    sortings: {
      poNumber: { label: i18n.t('procurement.purchase_orders.list.po_number') },
      description: { label: i18n.t('procurement.purchase_orders.list.description') },
      status: { label: i18n.t('procurement.purchase_orders.list.status') },
      date: { label: i18n.t('procurement.purchase_orders.list.requested') },
    },
    defaultSortings: { field: 'description', order: 'asc' },
    filters: {
      status: {
        label: i18n.t('procurement.purchase_orders.list.status'),
        type: 'in' as const,
        options: {
          options: [
            { label: i18n.t('procurement.purchase_orders.status.processing'), value: 'processing' },
            { label: i18n.t('procurement.purchase_orders.status.draft'), value: 'draft' },
            { label: i18n.t('procurement.purchase_orders.status.pending'), value: 'pending' },
            { label: i18n.t('procurement.purchase_orders.status.ordered'), value: 'ordered' },
            { label: i18n.t('procurement.purchase_orders.status.partial'), value: 'partial' },
            { label: i18n.t('procurement.purchase_orders.status.received'), value: 'received' },
            { label: i18n.t('procurement.purchase_orders.status.closed'), value: 'closed' },
          ],
        },
      },
      date: {
        label: i18n.t('procurement.purchase_orders.list.date'),
        type: 'date' as const,
        options: {
          mode: 'range' as const,
          view: 'day' as const,
        },
      },
      ...(vendor
        ? {}
        : {
            vendor: vendorFilter,
            statusPreset: {
              label: i18n.t('procurement.purchase_orders.list.status'),
              type: 'in' as const,
              hideSelector: true,
              options: {
                options: [
                  {
                    label: i18n.t('procurement.purchase_orders.status.processing'),
                    value: 'processing',
                  },
                  { label: i18n.t('procurement.purchase_orders.status.draft'), value: 'draft' },
                  { label: i18n.t('procurement.purchase_orders.status.pending'), value: 'pending' },
                  { label: i18n.t('procurement.purchase_orders.status.ordered'), value: 'ordered' },
                  { label: i18n.t('procurement.purchase_orders.status.partial'), value: 'partial' },
                  {
                    label: i18n.t('procurement.purchase_orders.status.received'),
                    value: 'received',
                  },
                  { label: i18n.t('procurement.purchase_orders.status.closed'), value: 'closed' },
                ],
              },
            },
          }),
    },
    ...(vendor
      ? {}
      : {
          presets: [
            {
              label: i18n.t('procurement.purchase_orders.tabs.all'),
              filter: { statusPreset: [] },
            },
            {
              label: i18n.t('procurement.purchase_orders.status.processing'),
              filter: { statusPreset: ['processing'] },
            },
            {
              label: i18n.t('procurement.purchase_orders.status.draft'),
              filter: { statusPreset: ['draft'] },
            },
            {
              label: i18n.t('procurement.purchase_orders.status.pending'),
              filter: { statusPreset: ['pending'] },
            },
            {
              label: i18n.t('procurement.purchase_orders.status.ordered'),
              filter: { statusPreset: ['ordered'] },
            },
            {
              label: i18n.t('procurement.purchase_orders.status.partial'),
              filter: { statusPreset: ['partial'] },
            },
            {
              label: i18n.t('procurement.purchase_orders.status.received'),
              filter: { statusPreset: ['received'] },
            },
            {
              label: i18n.t('procurement.purchase_orders.status.closed'),
              filter: { statusPreset: ['closed'] },
            },
          ],
        }),
    itemUrl: (item: PurchaseOrderItemFragment) =>
      item.status === 'processing'
        ? undefined
        : navigation.procurement.purchaseOrders.detail.fullPath({ id: item.id }),
    itemActions: (item: PurchaseOrderItemFragment) => [
      {
        label: i18n.t('procurement.purchase_orders.list.download_pdf'),
        onClick: () => handleDownloadPdf(item.id),
        enabled: item.status !== 'draft' && item.status !== 'processing',
      },
    ],
    ...(canExport
      ? {
          secondaryActions: () => [
            {
              label: i18n.t('procurement.purchase_orders.list.advanced_export'),
              icon: Download,
              onClick: () => setShowExportSidepanel(true),
            },
          ],
        }
      : {}),
  })

  const apolloClient = useApolloClient()

  useSubscription(PurchaseOrderUpdatedSubscriptionDocument, {
    onData: () => {
      apolloClient.refetchQueries({ include: [PurchaseOrdersDataCollectionDocument] })
    },
  })

  const vendorColumn = vendor
    ? []
    : [
        {
          id: 'vendor',
          label: i18n.t('procurement.purchase_orders.list.vendor'),
          render: (item: PurchaseOrderItemFragment) => ({
            type: 'text' as const,
            value: { text: contactFullName(item.vendor) },
          }),
        },
      ]

  return (
    <>
      <OneDataCollection
        id="procurement-purchase-orders/v1"
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              allowColumnHiding: true,
              columns: [
                {
                  id: 'poNumber',
                  label: i18n.t('procurement.purchase_orders.list.po_number'),
                  sorting: 'poNumber',
                  noHiding: true,
                  render: (item: PurchaseOrderItemFragment) => ({
                    type: 'text' as const,
                    value: { text: item.formattedPoNumber },
                  }),
                },
                {
                  id: 'description',
                  label: i18n.t('procurement.purchase_orders.list.description'),
                  sorting: 'description',
                  hidden: true,
                  render: (item: PurchaseOrderItemFragment) => ({
                    type: 'text' as const,
                    value: { text: item.description },
                  }),
                },
                ...vendorColumn,
                {
                  id: 'requester',
                  label: i18n.t('procurement.purchase_orders.list.requested_by'),
                  render: (item: PurchaseOrderItemFragment) => ({
                    type: 'person' as const,
                    value: {
                      firstName:
                        item.purchaseRequest?.requesterEmployee?.fullName?.split(' ')[0] ?? '',
                      lastName:
                        item.purchaseRequest?.requesterEmployee?.fullName
                          ?.split(' ')
                          .slice(1)
                          .join(' ') ?? '',
                      src: buildUrl(item.purchaseRequest?.requesterEmployee?.avatar?.url),
                    },
                  }),
                },
                {
                  id: 'type',
                  label: i18n.t('procurement.purchase_orders.list.type'),
                  render: (item: PurchaseOrderItemFragment) => ({
                    type: 'text' as const,
                    value: { text: item.purchaseRequest?.type?.name ?? '' },
                  }),
                },
                {
                  id: 'status',
                  label: i18n.t('procurement.purchase_orders.list.status'),
                  sorting: 'status',
                  render: (item: PurchaseOrderItemFragment) => ({
                    type: 'status' as const,
                    value: {
                      label: getPoStatusLabel(item.status),
                      status: PO_STATUS_VARIANT[item.status],
                    },
                  }),
                },
                {
                  id: 'cost',
                  label: i18n.t('procurement.purchase_orders.list.cost'),
                  align: 'right' as const,
                  render: (item: PurchaseOrderItemFragment) => ({
                    type: 'text' as const,
                    value: {
                      text: item.cost
                        ? MoneyWithCurrency({
                            amount: Number(item.cost.cents),
                            currency: item.cost.currency,
                            locale,
                          })
                        : '',
                    },
                  }),
                },
                {
                  id: 'date',
                  label: i18n.t('procurement.purchase_orders.list.requested'),
                  sorting: 'date',
                  hidden: true,
                  render: (item: PurchaseOrderItemFragment) => ({
                    type: 'text' as const,
                    value: { text: dateBackendDateTimeToUI(item.date) },
                  }),
                },
                {
                  id: 'legalEntity',
                  label: i18n.t('procurement.purchase_orders.list.legal_entity'),
                  hidden: true,
                  render: (item: PurchaseOrderItemFragment) => ({
                    type: 'text' as const,
                    value: { text: item.legalEntity?.legalName ?? '' },
                  }),
                },
              ],
              frozenColumns: 1 as const,
            },
          },
        ]}
        csvExport={{ filename: 'purchase_orders' }}
        emptyStates={{
          'no-data': {
            title: i18n.t('procurement.purchase_orders.empty.description_company'),
          },
        }}
      />
      {canExport && showExportSidepanel && (
        <ExportSidepanel
          resource="procurement.purchase_order"
          hasDynamicFields
          onClose={() => setShowExportSidepanel(false)}
        />
      )}
    </>
  )
}

export default PurchaseOrdersDataTable
